import React, {useCallback, useEffect, useMemo, useState} from 'react'
import './ModalCustomerSessions.css'
import {format} from 'date-fns'

const ModalCustomerSessions = ({closeModal, customerIdSessions, customerInfo}) => {
    const customerId = customerIdSessions;
    const [customerSessions, setCustomerSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState('');

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setErrors('');

        fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_logs/customer/' + customerId + '/view',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Unable to load session history.');
                }

                return res.json();
            })
            .then(data => {
                setCustomerSessions(Array.isArray(data) ? data : [])
            })
            .catch(error => {
                console.error(error);
                setErrors('Unable to load session history right now.');
            })
            .finally(() => {
                setIsLoading(false);
            });
        }, [customerId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        closeModal();
    }

    const showLoadedContent = !isLoading;

    const buildUsageSummary = useCallback((items, valueSelector) => {
        const usageMap = items.reduce((accumulator, item) => {
            const rawValue = valueSelector(item);
            const normalizedValue = String(rawValue ?? '').trim() || 'Unknown';
            accumulator[normalizedValue] = (accumulator[normalizedValue] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(usageMap)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }
                return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
            });
    }, []);

    const clientUsage = useMemo(
        () => buildUsageSummary(customerSessions, (item) => `${item?.client_first_name ?? ''} ${item?.client_last_name ?? ''}`.trim()),
        [buildUsageSummary, customerSessions]
    );

    const facilityUsage = useMemo(
        () => buildUsageSummary(customerSessions, (item) => item?.facility_name),
        [buildUsageSummary, customerSessions]
    );

    const podUsage = useMemo(
        () => buildUsageSummary(customerSessions, (item) => `${item?.pod_description ?? ''} (${item?.machine ?? ''})`),
        [buildUsageSummary, customerSessions]
    );

    const getUsageSizing = useCallback((usageItems, labelHeader) => {
        const maxLabelChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.label ?? '').length),
            labelHeader.length
        );
        const maxCountChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.count ?? '').length),
            'Count'.length
        );

        const labelWidthCh = Math.max(8, maxLabelChars + 1);
        const countWidthCh = Math.max(5, maxCountChars + 1);

        return {
            labelWidthCh,
            countWidthCh,
        };
    }, []);

    const clientUsageSizing = useMemo(
        () => getUsageSizing(clientUsage, 'Client'),
        [clientUsage, getUsageSizing]
    );

    const facilityUsageSizing = useMemo(
        () => getUsageSizing(facilityUsage, 'Facility'),
        [facilityUsage, getUsageSizing]
    );

    const podUsageSizing = useMemo(
        () => getUsageSizing(podUsage, 'Pod'),
        [getUsageSizing, podUsage]
    );

    const lastSessionDate = useMemo(() => {
        if (!Array.isArray(customerSessions) || customerSessions.length === 0) {
            return null;
        }

        const latestTimestamp = customerSessions.reduce((latest, session) => {
            const timestamp = Date.parse(session?.started);
            if (Number.isNaN(timestamp)) {
                return latest;
            }
            return Math.max(latest, timestamp);
        }, Number.NEGATIVE_INFINITY);

        if (!Number.isFinite(latestTimestamp)) {
            return null;
        }

        return format(new Date(latestTimestamp), 'MMM d, yyyy');
    }, [customerSessions]);

    const columnSizing = useMemo(() => {
        const measure = (value) => String(value ?? '').length;
        const rows = Array.isArray(customerSessions) ? customerSessions : [];

        const columns = [
            {
                key: 'date',
                header: 'Date',
                className: 'center',
                value: (row) => format(new Date(row.started), 'MMM d, yyyy'),
                minCh: 12,
                maxCh: 14,
            },
            {
                key: 'client',
                header: 'Client',
                className: 'left',
                value: (row) => `${row.client_first_name ?? ''} ${row.client_last_name ?? ''}`.trim(),
                minCh: 12,
                maxCh: 30,
            },
            {
                key: 'mins',
                header: 'Mins',
                className: 'center',
                value: (row) => row.total_mins,
                minCh: 6,
                maxCh: 8,
            },
            {
                key: 'protocol',
                header: 'Protocol',
                className: 'left',
                value: (row) => row.name,
                minCh: 12,
                maxCh: 30,
            },
            {
                key: 'facility',
                header: 'Facility',
                className: 'left',
                value: (row) => row.facility_name,
                minCh: 12,
                maxCh: 26,
            },
            {
                key: 'pod',
                header: 'Pod',
                className: 'left',
                value: (row) => `${row.pod_description ?? ''} (${row.machine ?? ''})`,
                minCh: 14,
                maxCh: 30,
            },
        ];

        const sizedColumns = columns.map((column) => {
            const longestContent = rows.reduce((longest, row) => {
                return Math.max(longest, measure(column.value(row)));
            }, measure(column.header));

            const paddedCh = longestContent + 1;
            const widthCh = Math.max(column.minCh, Math.min(column.maxCh, paddedCh));

            return {
                ...column,
                widthCh,
            };
        });

        const tableWidthCh = sizedColumns.reduce((total, column) => total + column.widthCh, 0);

        return {
            columns: sizedColumns,
            tableWidthCh,
        };
    }, [customerSessions]);

    const summaryWidthCh = useMemo(() => {
        const clientCardWidthCh = clientUsageSizing.labelWidthCh + clientUsageSizing.countWidthCh + 9;
        const facilityCardWidthCh = facilityUsageSizing.labelWidthCh + facilityUsageSizing.countWidthCh + 9;
        const podCardWidthCh = podUsageSizing.labelWidthCh + podUsageSizing.countWidthCh + 9;
        const cardsGapCh = 8;
        return clientCardWidthCh + facilityCardWidthCh + podCardWidthCh + cardsGapCh;
    }, [clientUsageSizing, facilityUsageSizing, podUsageSizing]);

    const modalContentWidthCh = Math.max(columnSizing.tableWidthCh, summaryWidthCh);

    const modalWidthStyle = {
        width: `min(calc(${modalContentWidthCh}ch + 5.4rem), 96vw)`,
    };

    const tableWidthStyle = {
        width: `${columnSizing.tableWidthCh}ch`,
    };

    return (
        <div className='modal-table'>
            <div className='modal-sessions modal-customer-history-sessions' style={modalWidthStyle}>
                <div className='modal-sessions-header'>
                    {showLoadedContent ? (
                        <>
                            <h3>Session History</h3>
                            <p>
                                {customerInfo?.customer_name} | Customer ID: {customerInfo?.id}
                            </p>
                            <div className='modal-sessions-chip-row'>
                                <div className='modal-sessions-count'>
                                    {customerSessions.length} {customerSessions.length === 1 ? 'session' : 'sessions'}
                                </div>
                                <div className='modal-sessions-count'>Last Session: {lastSessionDate ?? 'N/A'}</div>
                            </div>
                        </>
                    ) : (
                        <div className='modal-sessions-header-skeleton'>
                            <span className='table-skeleton-line modal-header-skeleton-title' />
                            <span className='table-skeleton-line modal-header-skeleton-subtitle' />
                            <span className='table-skeleton-line modal-header-skeleton-pill' />
                        </div>
                    )}
                </div>

                {errors && <div className='error'>{errors}</div>}

                {showLoadedContent && !errors && (
                    <div className='modal-sessions-usage-summary' style={{ flexWrap: 'nowrap', overflow: 'visible' }}>
                        <div className='usage-card'>
                            <div className='usage-card-title'>Clients ({clientUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {clientUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${clientUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${clientUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Client</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clientUsage.map((item) => (
                                                <tr key={`customer-client-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No client usage found.</span>
                                )}
                            </div>
                        </div>

                        <div className='usage-card'>
                            <div className='usage-card-title'>Facilities ({facilityUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {facilityUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${facilityUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${facilityUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Facility</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {facilityUsage.map((item) => (
                                                <tr key={`customer-facility-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No facility usage found.</span>
                                )}
                            </div>
                        </div>

                        <div className='usage-card'>
                            <div className='usage-card-title'>Pods ({podUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {podUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${podUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${podUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Pod</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {podUsage.map((item) => (
                                                <tr key={`customer-pod-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No pod usage found.</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className='table-wrapper modal-sessions-table-scroll'>
                    <table className='table-sessions' style={tableWidthStyle}>
                        <colgroup>
                            {columnSizing.columns.map((column) => (
                                <col key={column.key} style={{ width: `${column.widthCh}ch` }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {columnSizing.columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={column.key === 'facility' || column.key === 'pod' ? 'center' : column.className}
                                    >
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 7 }).map((_, rowIndex) => (
                                    <tr key={`customer-sessions-skeleton-${rowIndex}`} className='table-skeleton-row'>
                                        {columnSizing.columns.map((column) => (
                                            <td key={`${column.key}-${rowIndex}`}>
                                                <span className={`table-skeleton-line${column.key === 'date' || column.key === 'mins' ? ' short' : ''}`} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : customerSessions.length > 0 ? (
                                customerSessions.map((customerSession, i) => {
                                    return <tr key={i}>
                                        <td className='center'>{format(new Date(customerSession.started), 'MMM d, yyyy')}</td>
                                        <td className='left'>{customerSession.client_first_name} {customerSession.client_last_name}</td>
                                        <td className='center'>{customerSession.total_mins}</td>
                                        <td className='left'>{customerSession.name}</td>
                                        <td className='left'>{customerSession.facility_name}</td>
                                        <td className='left'>{customerSession.pod_description} ({customerSession.machine})</td>
                                    </tr>
                                })
                            ) : (
                                <tr>
                                    <td className='center modal-sessions-empty' colSpan={6}>No session history found for this customer.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <button type='submit' className='btn' onClick={handleSubmit}>Close</button>
            </div>
        </div>
  )
}

export default ModalCustomerSessions